/**
 * `/gallery/neutral-surfaces` exists so a browser can measure the neutral
 * surfaces ISSUE-633 fixes. `apps/app` unit tests run on happy-dom with no
 * Tailwind stylesheet, so `getComputedStyle` resolves nothing there. It is
 * signed in and linked from no navigation, following the `/gallery` and
 * `/gallery/marks` precedent, and is driven by
 * `tests/e2e/src/app/neutralSurfaceContrast.e2e.ts`.
 *
 * The file is named `gallery_.neutral-surfaces` so this route stays out of
 * `/gallery`'s tree: `/gallery` renders no Outlet, so a nested route would
 * never paint. Same trap `gallery_.marks.tsx` records in its own header
 * comment.
 */
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { AccountPoolsList } from "../features/accountHome/AccountPoolsList";
import { AccountTeamPlate } from "../features/accountHome/AccountTeamPlate";
import type { typeAwardChip } from "../features/awards/awardChips";
import { ZERO_AWARD_COUNTS } from "../features/awards/awardCounts";
import { MedalsBlock } from "../features/awards/MedalsBlock";
import { FanTeamPlate } from "../features/fanProfile/FanTeamPlate";
import type { typePoolCardModel } from "../features/pools/poolsListState";

// The container class the real parents share, copied verbatim from
// AccountPoolsList.tsx:43, MedalsBlock.tsx:46, AccountFavoriteTeamsRow.tsx:33
// and FanFavoriteTeamsRow.tsx:20, all four of which carry this identical
// string.
const SURFACE_CARD_CLASS =
  "flex flex-col gap-3 rounded-lg border border-line bg-surface p-4";

// markTone is hardcoded to "neutral" because poolMarkTone(poolId) hashes the
// pool id modulo six and a spec cannot ask for "neutral".
const NEUTRAL_POOL_CARDS: readonly typePoolCardModel[] = [
  {
    poolId: "neutral-pool-1",
    name: "Neutral Pool",
    initials: "NP",
    markTone: "neutral",
    leagueLabel: "NFL",
    contestLabel: "Survivor",
    membersLabel: "12 members",
    seasonLabel: "2025 season",
    statusLabel: "Active",
    statusTone: "ok",
    isSelectable: true,
    isPendingRequest: false,
  },
];

// The order here is load bearing: the spec indexes the rendered <li>
// elements by position, color7 first (neutralStrong) then color8
// (neutralSoft).
const NEUTRAL_AWARD_CHIPS: readonly typeAwardChip[] = [
  {
    awardGrantId: "neutral-chip-color7",
    poolId: "neutral-pool-1",
    contestName: null,
    kind: "medal",
    source: "custom",
    name: "Neutral Strong",
    color: "color7",
    isThisSeason: true,
  },
  {
    awardGrantId: "neutral-chip-color8",
    poolId: "neutral-pool-1",
    contestName: null,
    kind: "medal",
    source: "custom",
    name: "Neutral Soft",
    color: "color8",
    isThisSeason: true,
  },
];

const NeutralSurfacesGalleryScreen = (): ReactNode => (
  <div
    data-testid="neutral-surfaces-gallery"
    className="flex flex-col gap-6 p-4"
  >
    <h1 className="text-h1 text-ink">Neutral surface gallery</h1>
    <p className="text-body text-ink-secondary">
      Every neutral surface ISSUE-633 fixes. This page exists so a browser can
      measure them. It is not linked from any navigation.
    </p>

    <AccountPoolsList
      isLoading={false}
      hasFailed={false}
      cards={NEUTRAL_POOL_CARDS}
      testId="neutral-pools-list"
    />

    <MedalsBlock
      isLoading={false}
      hasFailed={false}
      thisSeason={ZERO_AWARD_COUNTS}
      allTime={ZERO_AWARD_COUNTS}
      chips={NEUTRAL_AWARD_CHIPS}
      title="Awards"
      thisSeasonLabel="This season"
      allTimeLabel="All time"
      tabsLabel="Award scope"
      unavailableMessage="Awards are unavailable."
      testId="neutral-medals-block"
    />

    <section data-testid="neutral-account-teams" className={SURFACE_CARD_CLASS}>
      <h2 className="text-h2 text-ink">Account team plate</h2>
      <AccountTeamPlate teamId="neutral-team-1" />
    </section>

    <section data-testid="neutral-fan-teams" className={SURFACE_CARD_CLASS}>
      <h2 className="text-h2 text-ink">Fan team plate</h2>
      <FanTeamPlate teamId="neutral-team-1" />
    </section>

    <section data-testid="neutral-probe-card" className={SURFACE_CARD_CLASS}>
      <h2 className="text-h2 text-ink">Unframed probe</h2>
      {/* This element is UNFRAMED ON PURPOSE and must never be given a
          border. tests/e2e/src/app/neutralSurfaceContrast.e2e.ts asserts
          two things about it at once: that its computed border-top-color
          scores at or above 3 against the card (the phantom currentColor
          reading ISSUE-632 describes), and that paintedBoundaryContrast
          scores it below 3 (the helper ignoring an unpainted border).
          Framing it would silently disarm the guard that keeps every other
          assertion on this page honest. */}
      <span
        data-testid="neutral-unframed-probe"
        className="rounded-full bg-surface-elevated px-2 py-1 text-caption text-ink"
      >
        Do not frame this
      </span>
    </section>
  </div>
);

export const Route = createFileRoute("/gallery_/neutral-surfaces")({
  beforeLoad: requireSignedIn,
  component: NeutralSurfacesGalleryScreen,
});
